import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import Button from "../Button";
import EditCell from "../EditCell";
import Input from "../Input";
import type { SheetCardContext, SheetChange, SheetColumn, SheetGroupBy, SheetOption, SheetRowId } from "./types";
import {
    blankValueFor, cellText, columnOptions, columnValue, exportSheetToExcel, formatDateText, formatNumberText,
    isBlank, isNumericColumn, isReadOnly, matchHeaderLine, parseCellText, parseClipboard, sameValue,
    sheetRowErrors, toClipboardText, toNumber,
} from "./sheetValues";
import SheetPopover from "./SheetPopover";
import classes from "./dataSheet.module.scss";

export interface DataSheetProps<R> {
    columns: SheetColumn<R>[];
    rows: R[];
    getRowId: (row: R) => SheetRowId;
    /** Screen-reader name of the grid ("Movements sheet"). */
    ariaLabel: string;
    /** Applies cell edits. Without it the sheet is read-only. */
    onCellsChange?: (changes: SheetChange[]) => void;
    /** Gold "unsaved" marker for a cell. */
    isCellChanged?: (row: R, key: string) => boolean;
    /** A whole-row rule (ends before it starts) — the row number turns red and the row can't save. */
    rowError?: (row: R) => string | null | undefined;
    /** New rows from a paste or from typing in the empty last row, as key → value records. */
    onAddRows?: (rows: Record<string, unknown>[]) => void;
    /** Hint in the empty last row ("Type here to add a movement"). */
    newRowLabel?: string;
    /** "Movement 7" — name box, tooltips, card title. */
    rowLabel?: (row: R) => string;
    /** Words for messages ("3 movements selected"). */
    noun?: { one: string; many: string };
    showRowNumbers?: boolean;
    /** Row-number cell content; default 1, 2, 3… */
    rowNumber?: (row: R, index: number) => React.ReactNode;
    /** "done" mutes the row's text; "live" marks its row number. */
    rowState?: (row: R) => "done" | "live" | null | undefined;
    /** Checkbox column (Shift-click selects a range). */
    selectable?: boolean;
    /** Rows that can't be picked get no checkbox and stay out of select-all (info rows). Default: every row. */
    canSelectRow?: (row: R) => boolean;
    /** The bulk bar's actions for the selected rows. */
    bulkActions?: (selected: R[], clear: () => void) => React.ReactNode;
    onSelectionChange?: (ids: SheetRowId[]) => void;
    groupBy?: SheetGroupBy<R>;
    showTotals?: boolean;
    /** Totals row: text in the first column when it has no total of its own. */
    totalsLabel?: (rows: R[]) => React.ReactNode;
    /** Totals row: muted text in the first run of columns without a total. */
    totalsNote?: (rows: R[]) => React.ReactNode;
    /** The bar above the grid showing the active cell and its value. */
    showNameBox?: boolean;
    toolbarStart?: React.ReactNode;
    toolbarEnd?: React.ReactNode;
    /** After Columns / Export to Excel — the area's primary action goes here so it sits last. */
    toolbarTrail?: React.ReactNode;
    /** Shows "Export to Excel"; the file name without .xlsx. */
    exportFileName?: string;
    /** Rows the export includes (default: every row as filtered and sorted). */
    isExportable?: (row: R) => boolean;
    /** A trailing column per row ("Open"). */
    rowActions?: (row: R) => React.ReactNode;
    rowActionsWidth?: number;
    /** "start": the row actions are the first column, frozen with the row number (an Open button that never scrolls away). */
    rowActionsAt?: "start" | "end";
    /** With rowActionsAt "start": more row buttons in a last column (e.g. a stamp button). */
    rowTrailActions?: (row: R) => React.ReactNode;
    rowTrailActionsWidth?: number;
    /** Phones: the card a tapped row opens. Default: a form built from the columns. */
    renderCard?: (row: R, ctx: SheetCardContext) => React.ReactNode;
    /** Phones: the columns the swipe grid shows, in this order (default: the visible columns). The card still edits `columns`. */
    phoneColumns?: SheetColumn<R>[];
    /**
     * Phones: the page's own buttons after "Back to the sheet" at the foot of
     * the default card (Open, Duplicate, Delete…). Every card keeps Back, so
     * all sheets close the same way. Ignored with renderCard.
     */
    cardActions?: (row: R, ctx: SheetCardContext) => React.ReactNode;
    /** Follows the active row (a side panel can show it). */
    onActiveRowChange?: (row: R | null) => void;
    /**
     * Moves the active cell to this row (opening its group, scrolling it into
     * view) whenever the value changes — e.g. "open this rate in the sheet"
     * from another view. An id no row has is ignored; to focus the same row
     * again, pass null first. Optional; the sheet otherwise owns its active cell.
     */
    focusRowId?: SheetRowId | null;
    emptyText?: string;
    /** Tests / previews: render the phone layout. */
    forcePhone?: boolean;
    className?: string;
}

interface Pos { r: number; c: number }

interface EditState {
    r: number;
    c: number;
    /** Typed text (text-like columns) or the filter (select columns). */
    draft: string;
    /** Draft value of a custom editor. */
    value: unknown;
    optionIndex: number;
    error: string | null;
    isNew: boolean;
    /** Date edit opened by F2 / double-click uses the native date field; typing uses a text field. */
    nativeDate: boolean;
}

interface PastedCell { text: string; value?: unknown; error?: string }

interface PendingPaste {
    lines: Record<string, PastedCell>[];
    keys: string[];
    byHeader: boolean;
    matched: number;
    unmatched: string[];
    errorCount: number;
}

interface UndoBatch { changes: SheetChange[]; prevs: SheetChange[] }

const CHECK_W = 36;
const NUMBER_W = 44;
const DEFAULT_W = 140;
const PHONE_QUERY = "(max-width: 575.98px)";

const cx = (...names: (string | false | null | undefined)[]) => names.filter(Boolean).join(" ");

const usePhone = (): boolean => {
    const read = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(PHONE_QUERY).matches;
    const [isPhone, setIsPhone] = useState(read);
    useEffect(() => {
        if (typeof window.matchMedia !== "function") return undefined;
        const query = window.matchMedia(PHONE_QUERY);
        const handleChange = () => setIsPhone(query.matches);
        query.addEventListener?.("change", handleChange);
        return () => query.removeEventListener?.("change", handleChange);
    }, []);
    return isPhone;
};

const TONE_CLASS: Record<string, string> = {
    neutral: classes.toneNeutral,
    info: classes.toneInfo,
    success: classes.toneSuccess,
    warning: classes.toneWarning,
    danger: classes.toneDanger,
};

/** What a cell shows at rest. */
const displayOf = <R,>(column: SheetColumn<R>, row: R, value: unknown): React.ReactNode => {
    if (isBlank(value)) return column.placeholder ?? (column.format ? column.format(value, row) : "—");
    if (column.format) return column.format(value, row);
    switch (column.type) {
        case "money": {
            const n = toNumber(value);
            if (n === null) return String(value);
            const currency = column.currency?.(row);
            return (
                <>
                    {currency && <span className={classes.currency}>{currency}</span>}
                    {formatNumberText(n, 2)}
                </>
            );
        }
        case "number": {
            const n = toNumber(value);
            return n === null ? String(value) : formatNumberText(n);
        }
        case "date":
            return formatDateText(value);
        case "select":
            return cellText(column, row, value);
        default:
            return String(value);
    }
};

/** The text an editor starts with when the user opens a cell (F2, Enter, double-click). */
const editTextOf = <R,>(column: SheetColumn<R>, row: R | undefined): string => {
    if (!row) return "";
    const value = columnValue(column, row);
    if (isBlank(value)) return "";
    if (column.type === "date" || column.type === "time" || !column.type || column.type === "text") return String(value);
    return cellText(column, row, value);
};

const filterOptions = (options: SheetOption[], draft: string): SheetOption[] => {
    const wanted = draft.trim().toLowerCase();
    return wanted ? options.filter((option) => option.label.toLowerCase().includes(wanted)) : options;
};

/**
 * The shared spreadsheet surface (rules doc 2026-10-05, "Sheet" pattern):
 * a dense grid with a frozen first column and sticky header, Excel keys
 * (arrows, Tab, Enter/F2/typing to edit, Esc, Ctrl+Z/Y, Ctrl+D, Delete,
 * Ctrl+C/V of ranges), paste-from-Excel with a preview of new rows,
 * checkbox selection with a bulk bar, day/place groups with subtotals, a
 * totals row, sort / filter / hide / resize from the headers, red-corner
 * errors and gold unsaved cells. Phones get the same rows in their own
 * scroll box; tapping a row opens it as a card form.
 *
 * Controlled: the page owns the rows and the saved values; the sheet
 * reports edits through onCellsChange / onAddRows.
 */
function DataSheet<R>({
    columns, rows, getRowId, ariaLabel, onCellsChange, isCellChanged, rowError, onAddRows, newRowLabel,
    rowLabel, noun = { one: "row", many: "rows" }, showRowNumbers = false, rowNumber, rowState, selectable = false, canSelectRow,
    bulkActions, onSelectionChange, groupBy, showTotals = false, totalsLabel, totalsNote, showNameBox = false,
    toolbarStart, toolbarEnd, toolbarTrail, exportFileName, isExportable, rowActions, rowActionsWidth = 72, rowActionsAt = "end", rowTrailActions, rowTrailActionsWidth = 120, renderCard, phoneColumns, cardActions, onActiveRowChange,
    focusRowId, emptyText, forcePhone, className,
}: DataSheetProps<R>) {
    const isPhone = usePhone() || !!forcePhone;
    const idBase = `sheet${useId().replace(/[^a-zA-Z0-9]/g, "")}`;

    const [active, setActive] = useState<Pos>({ r: 0, c: 0 });
    const [anchor, setAnchor] = useState<Pos | null>(null);
    const [editing, setEditingState] = useState<EditState | null>(null);
    const editingRef = useRef<EditState | null>(null);
    const setEditing = (next: EditState | null) => {
        editingRef.current = next;
        setEditingState(next);
    };
    const [hasFocus, setHasFocus] = useState(false);
    const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
    const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);
    const [filters, setFilters] = useState<Record<string, string>>({});
    const [hidden, setHidden] = useState<Set<string>>(() => new Set(columns.filter((column) => column.hidden).map((column) => column.key)));
    const [widths, setWidths] = useState<Record<string, number>>({});
    const [selected, setSelected] = useState<Set<SheetRowId>>(() => new Set());
    const lastCheckedRef = useRef<SheetRowId | null>(null);
    const undoRef = useRef<UndoBatch[]>([]);
    const redoRef = useRef<UndoBatch[]>([]);
    const [pending, setPending] = useState<PendingPaste | null>(null);
    const [showPreview, setShowPreview] = useState(false);
    const [notice, setNotice] = useState<string | null>(null);
    const [headMenu, setHeadMenu] = useState<string | null>(null);
    const [isColumnsOpen, setIsColumnsOpen] = useState(false);
    const [cardRowId, setCardRowId] = useState<SheetRowId | null>(null);
    const tableRef = useRef<HTMLTableElement>(null);
    const editorRef = useRef<HTMLDivElement | null>(null);
    const columnsMenuRef = useRef<HTMLDivElement>(null);
    const [editorNode, setEditorNode] = useState<HTMLDivElement | null>(null);
    const attachEditor = useCallback((node: HTMLDivElement | null) => {
        editorRef.current = node;
        setEditorNode(node);
    }, []);

    useEffect(() => {
        if (!notice) return undefined;
        const timer = window.setTimeout(() => setNotice(null), 4000);
        return () => window.clearTimeout(timer);
    }, [notice]);

    // ── Rows and columns as shown ───────────────────────────────────────
    const visibleColumns = useMemo(() => columns.filter((column) => !hidden.has(column.key)), [columns, hidden]);
    const widthOf = (column: SheetColumn<R>) => widths[column.key] ?? column.width ?? DEFAULT_W;
    let frozenCount = 0;
    while (frozenCount < visibleColumns.length && visibleColumns[frozenCount].frozen) frozenCount += 1;
    if (isPhone) frozenCount = Math.min(1, visibleColumns.length);
    const actionsFirst = !!rowActions && rowActionsAt === "start" && !isPhone;
    const leadWidth = isPhone ? 0 : (selectable ? CHECK_W : 0) + (showRowNumbers ? NUMBER_W : 0) + (actionsFirst ? rowActionsWidth : 0);
    const lefts: number[] = [];
    let leftAcc = leadWidth;
    visibleColumns.forEach((column, index) => {
        lefts.push(leftAcc);
        if (index < frozenCount) leftAcc += widthOf(column);
    });

    const filteredRows = useMemo(() => rows.filter((row) => Object.entries(filters).every(([key, text]) => {
        if (!text) return true;
        const column = columns.find((c) => c.key === key);
        return !column || cellText(column, row).toLowerCase().includes(text.toLowerCase());
    })), [rows, filters, columns]);

    const sortedRows = useMemo(() => {
        if (!sort) return filteredRows;
        const column = columns.find((c) => c.key === sort.key);
        if (!column) return filteredRows;
        const numeric = isNumericColumn(column);
        const keyed = filteredRows.map((row, index) => {
            const value = columnValue(column, row);
            return { row, index, n: numeric ? toNumber(value) : null, t: column.type === "date" || column.type === "time" ? String(value ?? "") : cellText(column, row, value), blank: isBlank(value) };
        });
        keyed.sort((a, b) => {
            if (a.blank !== b.blank) return a.blank ? 1 : -1;
            const cmp = numeric ? (a.n ?? 0) - (b.n ?? 0) : a.t.localeCompare(b.t, undefined, { numeric: true, sensitivity: "base" });
            return cmp * sort.dir || a.index - b.index;
        });
        return keyed.map((entry) => entry.row);
    }, [filteredRows, sort, columns]);

    const groups = useMemo(() => {
        if (!groupBy) return null;
        const order: string[] = [];
        const byKey = new Map<string, R[]>();
        sortedRows.forEach((row) => {
            const key = groupBy.key(row);
            if (!byKey.has(key)) {
                byKey.set(key, []);
                order.push(key);
            }
            byKey.get(key)!.push(row);
        });
        if (groupBy.compare) order.sort(groupBy.compare);
        return order.map((key) => ({ key, rows: byKey.get(key)! }));
    }, [groupBy, sortedRows]);

    const visibleRows = useMemo(
        () => (groups ? groups.flatMap((group) => (collapsed.has(group.key) ? [] : group.rows)) : sortedRows),
        [groups, collapsed, sortedRows],
    );

    const canEditSheet = !!onCellsChange;
    const hasNewRow = !!onAddRows && canEditSheet && !isPhone;
    const lastRowIndex = visibleRows.length - (hasNewRow ? 0 : 1);
    const clamp = (pos: Pos): Pos => ({
        r: Math.max(0, Math.min(Math.max(lastRowIndex, 0), pos.r)),
        c: Math.max(0, Math.min(visibleColumns.length - 1, pos.c)),
    });
    const safeActive = clamp(active);
    const activeRow = safeActive.r < visibleRows.length ? visibleRows[safeActive.r] : undefined;
    const activeColumn = visibleColumns[safeActive.c];
    const activeRowId = activeRow === undefined ? null : getRowId(activeRow);

    const errorsByRow = sheetRowErrors(rows, columns, getRowId, rowError);
    const labelOf = (row: R | undefined) => (row === undefined ? `New ${noun.one}` : rowLabel ? rowLabel(row) : `Row ${visibleRows.indexOf(row) + 1}`);

    const rowsById = useMemo(() => {
        const map = new Map<SheetRowId, R>();
        rows.forEach((row) => map.set(getRowId(row), row));
        return map;
    }, [rows, getRowId]);

    useEffect(() => {
        onActiveRowChange?.(activeRowId === null ? null : rowsById.get(activeRowId) ?? null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeRowId]);

    // A page asked for a row (focusRowId): open its group, then make it the
    // active row — once per request, so later collapsing doesn't jump back.
    const pendingFocusRef = useRef<SheetRowId | null>(null);
    useEffect(() => {
        pendingFocusRef.current = focusRowId ?? null;
    }, [focusRowId]);
    useEffect(() => {
        const target = pendingFocusRef.current;
        if (target === null) return undefined;
        const row = rowsById.get(target);
        if (row === undefined) {
            pendingFocusRef.current = null;
            return undefined;
        }
        const groupKey = groupBy ? groupBy.key(row) : null;
        if (groupKey !== null && collapsed.has(groupKey)) {
            setCollapsed((prev) => {
                const next = new Set(prev);
                next.delete(groupKey);
                return next;
            });
            return undefined; // runs again once the group's rows are visible
        }
        pendingFocusRef.current = null;
        const index = visibleRows.findIndex((candidate) => getRowId(candidate) === target);
        if (index < 0) return undefined; // filtered out
        setAnchor(null);
        setActive((prev) => ({ r: index, c: prev.c }));
        const timer = window.setTimeout(() => {
            document.getElementById(`${idBase}-r${index}-c0`)?.scrollIntoView?.({ block: "center", inline: "nearest" });
        }, 0);
        return () => window.clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusRowId, collapsed]);

    // Selection follows the rows (a deleted row leaves it).
    useEffect(() => {
        setSelected((prev) => {
            const next = new Set(Array.from(prev).filter((id) => rowsById.has(id)));
            return next.size === prev.size ? prev : next;
        });
    }, [rowsById]);

    // The active cell stays in view while the arrows move it.
    useEffect(() => {
        if (!hasFocus) return;
        const cell = document.getElementById(`${idBase}-r${safeActive.r}-c${safeActive.c}`);
        cell?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
    }, [safeActive.r, safeActive.c, hasFocus, idBase]);

    // Focus the editor when a cell opens.
    const editKey = editing ? `${editing.r}:${editing.c}` : "";
    useEffect(() => {
        if (!editKey || !editorRef.current) return;
        const field = editorRef.current.querySelector<HTMLElement>("input:not([type=hidden]), select, textarea, [tabindex]:not([tabindex='-1'])");
        if (!field) return;
        field.focus({ preventScroll: true });
        if (field instanceof HTMLInputElement && field.type === "text") {
            const end = field.value.length;
            try { field.setSelectionRange(end, end); } catch { /* not a text field */ }
        }
    }, [editKey]);

    // ── Edits ───────────────────────────────────────────────────────────
    const focusGrid = () => {
        const table = tableRef.current;
        if (table && document.activeElement !== table) table.focus({ preventScroll: true });
    };
    const refocusSoon = () => window.setTimeout(focusGrid, 0);

    const applyChanges = (changes: SheetChange[], record = true) => {
        if (!onCellsChange) return;
        const real = changes.filter((change) => {
            const row = rowsById.get(change.rowId);
            const column = columns.find((c) => c.key === change.key);
            return row !== undefined && column !== undefined && !sameValue(columnValue(column, row), change.value);
        });
        if (!real.length) return;
        if (record) {
            const prevs = real.map((change) => {
                const column = columns.find((c) => c.key === change.key)!;
                return { rowId: change.rowId, key: change.key, value: columnValue(column, rowsById.get(change.rowId)!) };
            });
            undoRef.current.push({ changes: real, prevs });
            if (undoRef.current.length > 100) undoRef.current.shift();
            redoRef.current = [];
        }
        onCellsChange(real);
    };

    const undo = () => {
        const batch = undoRef.current.pop();
        if (!batch || !onCellsChange) return;
        redoRef.current.push(batch);
        onCellsChange(batch.prevs);
        setNotice(`Undid ${batch.changes.length === 1 ? "1 cell" : `${batch.changes.length} cells`}`);
    };
    const redo = () => {
        const batch = redoRef.current.pop();
        if (!batch || !onCellsChange) return;
        undoRef.current.push(batch);
        onCellsChange(batch.changes);
    };

    const canEditCell = (pos: Pos): boolean => {
        const column = visibleColumns[pos.c];
        if (!column || !canEditSheet) return false;
        if (pos.r >= visibleRows.length) return hasNewRow && !isReadOnly(column, undefined);
        return !isReadOnly(column, visibleRows[pos.r]);
    };

    const startEdit = (pos: Pos, seed: string | null) => {
        if (!canEditCell(pos)) return;
        const column = visibleColumns[pos.c];
        const isNew = pos.r >= visibleRows.length;
        const row = isNew ? undefined : visibleRows[pos.r];
        let optionIndex = 0;
        if (column.type === "select" && seed === null && row) {
            const value = columnValue(column, row);
            optionIndex = Math.max(0, columnOptions(column, row).findIndex((option) => String(option.value) === String(value)));
        }
        setActive(pos);
        setAnchor(null);
        setEditing({
            r: pos.r,
            c: pos.c,
            draft: seed ?? (column.type === "select" ? "" : editTextOf(column, row)),
            value: row ? columnValue(column, row) : undefined,
            optionIndex,
            error: null,
            isNew,
            nativeDate: seed === null,
        });
    };

    /** Commits the open editor; false when the text doesn't fit the column (the editor stays open). */
    const commitEdit = (dr = 0, dc = 0, override?: { value: unknown }, refocus = true): boolean => {
        const ed = editingRef.current;
        if (!ed) return true;
        const column = visibleColumns[ed.c];
        if (!column) {
            setEditing(null);
            return true;
        }
        const row = ed.isNew ? undefined : visibleRows[ed.r];
        let value: unknown;
        if (override) {
            value = override.value;
        } else if (column.editor) {
            value = ed.value;
        } else if (column.type === "select") {
            const options = filterOptions(columnOptions(column, row), ed.draft);
            const pick = options[ed.optionIndex];
            if (pick) value = pick.value;
            else if (!ed.draft.trim()) value = row ? columnValue(column, row) : null;
            else {
                setEditing({ ...ed, error: `No “${ed.draft.trim()}” in the ${column.label} list.` });
                return false;
            }
        } else {
            const parsed = parseCellText(column, ed.draft, row);
            if ("error" in parsed) {
                setEditing({ ...ed, error: parsed.error });
                return false;
            }
            value = parsed.value;
        }
        setEditing(null);
        if (ed.isNew) {
            if (!isBlank(value)) {
                onAddRows?.([{ [column.key]: value }]);
                setNotice(`Added a ${noun.one}`);
            }
        } else if (row !== undefined) {
            applyChanges([{ rowId: getRowId(row), key: column.key, value }]);
        }
        if (dr || dc) {
            setActive(clamp({ r: ed.r + dr, c: ed.c + dc }));
            setAnchor(null);
        }
        if (refocus) refocusSoon();
        return true;
    };

    const cancelEdit = () => {
        if (!editingRef.current) return;
        setEditing(null);
        refocusSoon();
    };

    // ── Ranges ──────────────────────────────────────────────────────────
    const range = (() => {
        const a = anchor ? clamp(anchor) : safeActive;
        const lastData = Math.max(0, visibleRows.length - 1);
        return {
            r1: Math.min(Math.min(a.r, safeActive.r), lastData),
            r2: Math.min(Math.max(a.r, safeActive.r), lastData),
            c1: Math.min(a.c, safeActive.c),
            c2: Math.max(a.c, safeActive.c),
        };
    })();
    const isMultiCell = !!anchor && (range.r2 > range.r1 || range.c2 > range.c1);
    const inRange = (r: number, c: number) => isMultiCell && r >= range.r1 && r <= range.r2 && c >= range.c1 && c <= range.c2;

    const moveTo = (pos: Pos, extend: boolean) => {
        if (extend) setAnchor((prev) => prev ?? safeActive);
        else setAnchor(null);
        setActive(clamp(pos));
    };

    const clearRange = () => {
        const changes: SheetChange[] = [];
        for (let r = range.r1; r <= range.r2 && r < visibleRows.length; r += 1) {
            for (let c = range.c1; c <= range.c2; c += 1) {
                const column = visibleColumns[c];
                const row = visibleRows[r];
                if (column && !isReadOnly(column, row)) changes.push({ rowId: getRowId(row), key: column.key, value: blankValueFor(column) });
            }
        }
        applyChanges(changes);
    };

    const fillDown = () => {
        const changes: SheetChange[] = [];
        const top = isMultiCell ? range.r1 : safeActive.r - 1;
        const bottom = isMultiCell ? range.r2 : safeActive.r;
        if (top < 0 || bottom >= visibleRows.length) return;
        for (let c = range.c1; c <= range.c2; c += 1) {
            const column = visibleColumns[c];
            const source = columnValue(column, visibleRows[top]);
            for (let r = top + 1; r <= bottom; r += 1) {
                if (!isReadOnly(column, visibleRows[r])) changes.push({ rowId: getRowId(visibleRows[r]), key: column.key, value: source });
            }
        }
        applyChanges(changes);
    };

    // ── Clipboard ───────────────────────────────────────────────────────
    const parseLine = (line: string[], keys: (string | null)[]): Record<string, PastedCell> => {
        const record: Record<string, PastedCell> = {};
        keys.forEach((key, index) => {
            if (!key) return;
            const column = columns.find((c) => c.key === key);
            const text = line[index] ?? "";
            if (!column) return;
            const parsed = parseCellText(column, text);
            record[key] = "error" in parsed ? { text, error: parsed.error } : { text, value: parsed.value };
        });
        return record;
    };

    const openPending = (lines: string[][], keys: (string | null)[], byHeader: boolean, unmatched: string[]) => {
        const usedKeys = keys.filter((key): key is string => !!key);
        if (!lines.length || !usedKeys.length) return;
        const parsed = lines.map((line) => parseLine(line, keys));
        const errorCount = parsed.reduce((total, record) => total + Object.values(record).filter((cell) => cell.error).length, 0);
        setPending({ lines: parsed, keys: usedKeys, byHeader, matched: usedKeys.length, unmatched, errorCount });
        setShowPreview(false);
    };

    const addPending = () => {
        if (!pending || !onAddRows) return;
        const records = pending.lines.map((line) => {
            const record: Record<string, unknown> = {};
            Object.entries(line).forEach(([key, cell]) => {
                if (!cell.error && !isBlank(cell.value)) record[key] = cell.value;
            });
            return record;
        }).filter((record) => Object.keys(record).length > 0);
        if (records.length) onAddRows(records);
        setNotice(`Added ${records.length} ${records.length === 1 ? noun.one : noun.many}`);
        setPending(null);
        setShowPreview(false);
    };

    const pasteText = (text: string) => {
        if (!canEditSheet) return;
        const grid = parseClipboard(text);
        if (!grid.length) return;
        if (onAddRows) {
            const header = matchHeaderLine(grid[0], columns);
            if (header.isHeader) {
                openPending(grid.slice(1), header.keys, true, header.unmatched);
                return;
            }
        }
        const start = safeActive;
        const widest = Math.max(...grid.map((line) => line.length));
        const positional = (from: number) => Array.from({ length: widest }, (_, index) => visibleColumns[from + index]?.key ?? null);
        if (start.r >= visibleRows.length) {
            if (onAddRows) openPending(grid, positional(start.c), false, []);
            return;
        }
        let skipped = 0;
        const changes: SheetChange[] = [];
        const tryCell = (r: number, c: number, text: string) => {
            const column = visibleColumns[c];
            const row = visibleRows[r];
            if (!column || !row || isReadOnly(column, row)) { skipped += 1; return; }
            const parsed = parseCellText(column, text, row);
            if ("error" in parsed) { skipped += 1; return; }
            changes.push({ rowId: getRowId(row), key: column.key, value: parsed.value });
        };
        if (grid.length === 1 && grid[0].length === 1 && isMultiCell) {
            for (let r = range.r1; r <= range.r2; r += 1) for (let c = range.c1; c <= range.c2; c += 1) tryCell(r, c, grid[0][0]);
        } else {
            const fit = Math.min(grid.length, visibleRows.length - start.r);
            for (let i = 0; i < fit; i += 1) grid[i].forEach((text, j) => tryCell(start.r + i, start.c + j, text));
            setAnchor(start);
            setActive(clamp({ r: start.r + fit - 1, c: start.c + widest - 1 }));
            const rest = grid.slice(fit);
            if (rest.length && onAddRows) openPending(rest, positional(start.c), false, []);
        }
        applyChanges(changes);
        const pasted = changes.length;
        setNotice(`Pasted ${pasted} cell${pasted === 1 ? "" : "s"}${skipped ? ` · ${skipped} skipped (didn't fit the column)` : ""}`);
    };

    const copyRange = (): string => {
        const grid: string[][] = [];
        for (let r = range.r1; r <= range.r2 && r < visibleRows.length; r += 1) {
            const line: string[] = [];
            for (let c = range.c1; c <= range.c2; c += 1) line.push(cellText(visibleColumns[c], visibleRows[r]));
            grid.push(line);
        }
        return toClipboardText(grid);
    };

    // Only the grid itself — not a header filter field or a checkbox inside it.
    const isGridTarget = (event: React.SyntheticEvent) => event.target === tableRef.current;

    const handleCopy = (event: React.ClipboardEvent) => {
        if (editingRef.current || !visibleRows.length || !isGridTarget(event)) return;
        event.preventDefault();
        event.clipboardData.setData("text/plain", copyRange());
        const cells = (range.r2 - range.r1 + 1) * (range.c2 - range.c1 + 1);
        setNotice(`Copied ${cells} cell${cells === 1 ? "" : "s"}`);
    };
    const handleCut = (event: React.ClipboardEvent) => {
        if (editingRef.current || !visibleRows.length || !canEditSheet || !isGridTarget(event)) return;
        handleCopy(event);
        clearRange();
    };
    const handlePaste = (event: React.ClipboardEvent) => {
        if (editingRef.current || !isGridTarget(event)) return;
        event.preventDefault();
        pasteText(event.clipboardData.getData("text/plain"));
    };

    // ── Keyboard ────────────────────────────────────────────────────────
    const handleKeyDown = (event: React.KeyboardEvent<HTMLTableElement>) => {
        const key = event.key;
        const mod = event.ctrlKey || event.metaKey;
        const ed = editingRef.current;
        if (ed) {
            const column = visibleColumns[ed.c];
            if (key === "Escape") {
                if (!event.defaultPrevented) {
                    event.preventDefault();
                    cancelEdit();
                }
                return;
            }
            if (column?.type === "select" && !column.editor && (key === "ArrowDown" || key === "ArrowUp")) {
                event.preventDefault();
                const options = filterOptions(columnOptions(column, ed.isNew ? undefined : visibleRows[ed.r]), ed.draft);
                if (options.length) setEditing({ ...ed, optionIndex: (ed.optionIndex + (key === "ArrowDown" ? 1 : options.length - 1)) % options.length, error: null });
                return;
            }
            if (key === "Enter" && !event.defaultPrevented && !event.altKey) {
                event.preventDefault();
                commitEdit(event.shiftKey ? -1 : 1, 0);
                return;
            }
            if (key === "Tab") {
                event.preventDefault();
                commitEdit(0, event.shiftKey ? -1 : 1);
                return;
            }
            const plainText = column && !column.editor && column.type !== "select" && !(column.type === "date" && ed.nativeDate);
            if (plainText && (key === "ArrowDown" || key === "ArrowUp")) {
                event.preventDefault();
                commitEdit(key === "ArrowDown" ? 1 : -1, 0);
            }
            return;
        }
        if (event.target !== event.currentTarget && (event.target as HTMLElement).tagName === "INPUT") return; // a checkbox
        if (!visibleColumns.length) return;
        const moves: Record<string, [number, number]> = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1] };
        if (moves[key]) {
            event.preventDefault();
            const [dr, dc] = moves[key];
            const far = mod ? 10000 : 1;
            moveTo({ r: safeActive.r + dr * far, c: safeActive.c + dc * far }, event.shiftKey);
            return;
        }
        if (key === "Tab") {
            const nextC = safeActive.c + (event.shiftKey ? -1 : 1);
            if (nextC < 0 || nextC >= visibleColumns.length) return; // leave the grid
            event.preventDefault();
            moveTo({ r: safeActive.r, c: nextC }, false);
            return;
        }
        if (key === "Home" || key === "End") {
            event.preventDefault();
            const c = key === "Home" ? 0 : visibleColumns.length - 1;
            moveTo({ r: mod ? (key === "Home" ? 0 : lastRowIndex) : safeActive.r, c }, event.shiftKey);
            return;
        }
        if (key === "PageDown" || key === "PageUp") {
            event.preventDefault();
            moveTo({ r: safeActive.r + (key === "PageDown" ? 10 : -10), c: safeActive.c }, event.shiftKey);
            return;
        }
        if (key === "Enter" || key === "F2") {
            event.preventDefault();
            if (canEditCell(safeActive)) startEdit(safeActive, null);
            else if (key === "Enter") moveTo({ r: safeActive.r + (event.shiftKey ? -1 : 1), c: safeActive.c }, false);
            return;
        }
        if (key === "Escape") {
            if (anchor) {
                event.preventDefault();
                setAnchor(null);
            }
            return;
        }
        if (mod && (key === "z" || key === "Z") && !event.shiftKey) {
            event.preventDefault();
            undo();
            return;
        }
        if (mod && (key === "y" || key === "Y" || ((key === "z" || key === "Z") && event.shiftKey))) {
            event.preventDefault();
            redo();
            return;
        }
        if (mod && (key === "d" || key === "D")) {
            event.preventDefault();
            fillDown();
            return;
        }
        if (mod && (key === "a" || key === "A")) {
            event.preventDefault();
            setAnchor({ r: 0, c: 0 });
            setActive(clamp({ r: visibleRows.length - 1, c: visibleColumns.length - 1 }));
            return;
        }
        if ((key === "Delete" || key === "Backspace") && canEditSheet) {
            event.preventDefault();
            clearRange();
            return;
        }
        if (key.length === 1 && !mod && !event.altKey && canEditCell(safeActive)) {
            event.preventDefault();
            startEdit(safeActive, key);
        }
    };

    const handleCellMouseDown = (event: React.MouseEvent, r: number, c: number) => {
        const ed = editingRef.current;
        if (ed && ed.r === r && ed.c === c) return; // inside the open editor
        if (event.button !== 0) return;
        event.preventDefault();
        if (ed && !commitEdit(0, 0, undefined, false)) return;
        if (event.shiftKey) setAnchor((prev) => prev ?? safeActive);
        else setAnchor(null);
        setActive({ r, c });
        focusGrid();
    };

    const handleBlur = (event: React.FocusEvent<HTMLTableElement>) => {
        if (tableRef.current?.contains(event.relatedTarget as Node | null)) return;
        setHasFocus(false);
        const ed = editingRef.current;
        if (ed && !visibleColumns[ed.c]?.editor) commitEdit(0, 0, undefined, false);
    };

    // ── Selection ───────────────────────────────────────────────────────
    const commitSelection = (next: Set<SheetRowId>) => {
        setSelected(next);
        onSelectionChange?.(Array.from(next));
    };
    const pickableIds = (list: R[]) => (canSelectRow ? list.filter(canSelectRow) : list).map(getRowId);
    const toggleRow = (id: SheetRowId, shift: boolean) => {
        const next = new Set(selected);
        const willSelect = !selected.has(id);
        const ids = pickableIds(visibleRows);
        const from = lastCheckedRef.current === null ? -1 : ids.indexOf(lastCheckedRef.current);
        const to = ids.indexOf(id);
        if (shift && from >= 0 && to >= 0) {
            ids.slice(Math.min(from, to), Math.max(from, to) + 1).forEach((rowId) => (willSelect ? next.add(rowId) : next.delete(rowId)));
        } else if (willSelect) next.add(id);
        else next.delete(id);
        lastCheckedRef.current = id;
        commitSelection(next);
    };
    const toggleMany = (ids: SheetRowId[]) => {
        const all = ids.length > 0 && ids.every((id) => selected.has(id));
        const next = new Set(selected);
        ids.forEach((id) => (all ? next.delete(id) : next.add(id)));
        commitSelection(next);
    };
    const clearSelection = () => commitSelection(new Set());
    const selectedRows = rows.filter((row) => selected.has(getRowId(row)));

    // ── Header tools ────────────────────────────────────────────────────
    const startResize = (event: React.PointerEvent, column: SheetColumn<R>) => {
        event.preventDefault();
        event.stopPropagation();
        const startX = event.clientX;
        const startWidth = widthOf(column);
        const handleMove = (moveEvent: PointerEvent) => setWidths((prev) => ({ ...prev, [column.key]: Math.max(56, Math.round(startWidth + moveEvent.clientX - startX)) }));
        const handleUp = () => {
            window.removeEventListener("pointermove", handleMove);
            window.removeEventListener("pointerup", handleUp);
        };
        window.addEventListener("pointermove", handleMove);
        window.addEventListener("pointerup", handleUp);
    };
    const toggleHidden = (key: string) => setHidden((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else if (columns.length - next.size > 1) next.add(key);
        return next;
    });

    const activeFilters = Object.entries(filters).filter(([, text]) => !!text);

    // ── Pieces ──────────────────────────────────────────────────────────
    const frozenStyle = (index: number): React.CSSProperties | undefined => (index < frozenCount ? { left: lefts[index] } : undefined);
    const frozenClass = (index: number) => cx(index < frozenCount && classes.frozen, index === frozenCount - 1 && classes.frozenEdge);
    const showRing = hasFocus || !!editing;

    const renderEditor = (row: R | undefined, column: SheetColumn<R>, ed: EditState) => {
        const update = (patch: Partial<EditState>) => {
            if (editingRef.current) setEditing({ ...editingRef.current, ...patch });
        };
        let field: React.ReactNode;
        let options: SheetOption[] = [];
        if (column.editor && row !== undefined) {
            field = column.editor({
                row,
                value: ed.value,
                onChange: (value) => update({ value }),
                onCommit: (value) => { commitEdit(0, 0, value === undefined ? undefined : { value }); },
                onCancel: cancelEdit,
            });
        } else if (column.type === "select") {
            options = filterOptions(columnOptions(column, row), ed.draft);
            field = (
                <Input
                    type="text"
                    id={`${idBase}-edit`}
                    label={`Find ${column.label.toLowerCase()}…`}
                    value={ed.draft}
                    className="form-control"
                    onChange={(_name, value) => update({ draft: String(value), optionIndex: 0, error: null })}
                />
            );
        } else {
            field = (
                <Input
                    type={column.type === "date" && ed.nativeDate ? "date" : "text"}
                    id={`${idBase}-edit`}
                    label={column.label}
                    value={ed.draft}
                    className="form-control"
                    onChange={(_name, value) => update({ draft: String(value), error: null })}
                />
            );
        }
        const alignEnd = column.align === "end" || (column.align === undefined && isNumericColumn(column));
        return (
            <div ref={attachEditor} className={classes.editor}>
                <EditCell onRevert={cancelEdit} align={alignEnd ? "end" : "start"} title={column.title}>
                    {field}
                </EditCell>
                {column.type === "select" && !column.editor && (
                    <SheetPopover anchor={editorNode} matchWidth maxHeight={300} className={classes.options} role="listbox" aria-label={`${column.label} choices`}>
                        {ed.error && <div className={classes.optionsError} role="alert">{ed.error}</div>}
                        <div className={classes.optionsHint}>Type to filter · ↑↓ then Enter · Esc keeps the old value</div>
                        {options.map((option, index) => (
                            <button
                                key={String(option.value)}
                                type="button"
                                role="option"
                                aria-selected={index === ed.optionIndex}
                                className={cx("btn-unstyled", classes.option, index === ed.optionIndex && classes.optionOn)}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => commitEdit(0, 0, { value: option.value })}
                            >
                                <span>{option.label}</span>
                                {option.hint && <span className={cx(classes.optionHint, option.isWarning && classes.optionWarn)}>{option.hint}</span>}
                            </button>
                        ))}
                        {!options.length && <div className={classes.optionsHint}>No match — Esc keeps the old value</div>}
                    </SheetPopover>
                )}
                {ed.error && (column.type !== "select" || column.editor) && (
                    <SheetPopover anchor={editorNode} matchWidth className={classes.editError} role="alert">{ed.error}</SheetPopover>
                )}
            </div>
        );
    };

    const renderCell = (row: R, r: number, column: SheetColumn<R>, c: number) => {
        const value = columnValue(column, row);
        const isActive = safeActive.r === r && safeActive.c === c;
        const ed = editing && editing.r === r && editing.c === c && !editing.isNew ? editing : null;
        const problem = errorsByRow.get(getRowId(row))?.find((p) => p.key === column.key);
        const tone = column.tone?.(value, row);
        const readOnly = !canEditSheet || isReadOnly(column, row);
        const alignEnd = column.align === "end" || (column.align === undefined && isNumericColumn(column));
        const blank = isBlank(value);
        return (
            <td
                key={column.key}
                id={`${idBase}-r${r}-c${c}`}
                role="gridcell"
                aria-selected={isActive}
                aria-readonly={readOnly || undefined}
                className={cx(frozenClass(c), inRange(r, c) && classes.inRange, ed && classes.editing)}
                style={frozenStyle(c)}
                title={problem?.message}
                onMouseDown={(event) => handleCellMouseDown(event, r, c)}
                onDoubleClick={() => startEdit({ r, c }, null)}
            >
                {ed ? renderEditor(row, column, ed) : (
                    <div
                        className={cx(
                            classes.cell,
                            alignEnd && classes.alignEnd,
                            readOnly && classes.readOnly,
                            tone && TONE_CLASS[tone],
                            tone && classes.toned,
                            blank && (column.emptyIsWarning ? classes.warnText : classes.mutedText),
                            isCellChanged?.(row, column.key) && classes.changed,
                            problem && classes.error,
                            isActive && showRing && classes.active,
                        )}
                    >
                        <span className={classes.text}>{displayOf(column, row, value)}</span>
                        {isActive && showRing && !readOnly && <span className={classes.handle} aria-hidden="true" />}
                    </div>
                )}
                {problem && isActive && hasFocus && !ed && (
                    <SheetPopover anchorId={`${idBase}-r${r}-c${c}`} prefer="above" className={classes.tip} role="tooltip">
                        <strong>{labelOf(row)} can't save.</strong> {problem.message}
                    </SheetPopover>
                )}
            </td>
        );
    };

    const renderLeadCells = (kind: "header" | "row" | "group" | "new" | "total", row?: R, r?: number, groupRows?: R[]) => {
        if (isPhone) return null;
        const cells: React.ReactNode[] = [];
        if (selectable) {
            let content: React.ReactNode = null;
            if (kind === "header") {
                const ids = pickableIds(filteredRows);
                content = (
                    <input type="checkbox" className={classes.check} aria-label={`Select all ${noun.many}`}
                        checked={ids.length > 0 && ids.every((id) => selected.has(id))} onChange={() => toggleMany(ids)} />
                );
            } else if (kind === "group" && groupRows) {
                const ids = pickableIds(groupRows);
                content = ids.length === 0 ? null : (
                    <input type="checkbox" className={classes.check} aria-label={`Select the ${noun.many} in this group`}
                        checked={ids.every((id) => selected.has(id))} onChange={() => toggleMany(ids)} />
                );
            } else if (kind === "row" && row !== undefined && (!canSelectRow || canSelectRow(row))) {
                const id = getRowId(row);
                content = (
                    <input type="checkbox" className={classes.check} aria-label={`Select ${labelOf(row)}`} checked={selected.has(id)}
                        onChange={() => undefined} onClick={(event) => toggleRow(id, event.shiftKey)} />
                );
            }
            const Tag = kind === "header" ? "th" : "td";
            cells.push(<Tag key="check" className={cx(classes.lead, classes.frozen)} style={{ left: 0 }}>{content}</Tag>);
        }
        if (showRowNumbers) {
            const left = selectable ? CHECK_W : 0;
            const edge = frozenCount === 0 && !actionsFirst ? classes.frozenEdge : undefined;
            if (kind === "header") {
                cells.push(<th key="num" className={cx(classes.lead, classes.rowNum, classes.frozen, edge)} style={{ left }} scope="col">#</th>);
            } else if (kind === "row" && row !== undefined && r !== undefined) {
                const problems = errorsByRow.get(getRowId(row));
                const state = rowState?.(row);
                cells.push(
                    <td
                        key="num"
                        className={cx(classes.lead, classes.rowNum, classes.frozen, edge, problems && classes.rowNumError,
                            state === "live" && classes.rowNumLive, showRing && safeActive.r === r && classes.rowNumOn)}
                        style={{ left }}
                        title={problems ? `Can't save: ${problems.map((p) => p.message).join(" ")}` : state === "live" ? "Under way now" : undefined}
                    >
                        {rowNumber ? rowNumber(row, r) : r + 1}
                    </td>,
                );
            } else {
                cells.push(
                    <td key="num" className={cx(classes.lead, classes.rowNum, classes.frozen, edge)} style={{ left }}>
                        {kind === "new" ? "+" : kind === "total" ? "Σ" : null}
                    </td>,
                );
            }
        }
        if (actionsFirst) {
            const left = (selectable ? CHECK_W : 0) + (showRowNumbers ? NUMBER_W : 0);
            const edge = frozenCount === 0 ? classes.frozenEdge : undefined;
            if (kind === "header") cells.push(<th key="act" className={cx(classes.lead, classes.frozen, edge)} style={{ left }} scope="col"><span className="visually-hidden">Actions</span></th>);
            else cells.push(<td key="act" className={cx(classes.actionsCell, classes.frozen, edge)} style={{ left, textAlign: "start" }}>{kind === "row" && row !== undefined ? rowActions!(row) : null}</td>);
        }
        return cells;
    };

    /** Group header / totals row: label in the first column, per-column figures, empty runs merged with the note. */
    const renderSummaryCells = (figureFor: (column: SheetColumn<R>) => React.ReactNode, label: React.ReactNode, note: React.ReactNode) => {
        const cells: React.ReactNode[] = [];
        if (!visibleColumns.length) return cells;
        cells.push(<td key="label" className={cx(frozenClass(0), classes.summaryLabel)} style={frozenStyle(0)}>{label}</td>);
        let noteShown = !note;
        let i = 1;
        const isEmpty = (content: React.ReactNode) => content === undefined || content === null || content === false || content === "";
        while (i < visibleColumns.length) {
            const content = figureFor(visibleColumns[i]);
            if (i < frozenCount || !isEmpty(content)) {
                const alignEnd = visibleColumns[i].align === "end" || (visibleColumns[i].align === undefined && isNumericColumn(visibleColumns[i]));
                cells.push(<td key={visibleColumns[i].key} className={cx(frozenClass(i), classes.summaryFigure, alignEnd && classes.alignEnd)} style={frozenStyle(i)}>{content}</td>);
                i += 1;
                continue;
            }
            let j = i;
            while (j < visibleColumns.length && isEmpty(figureFor(visibleColumns[j]))) j += 1;
            cells.push(<td key={visibleColumns[i].key} colSpan={j - i} className={classes.summaryNote}>{noteShown ? null : note}</td>);
            noteShown = true;
            i = j;
        }
        return cells;
    };

    const groupLabelText = (groupKey: string, groupRows: R[]) => groupBy!.label(groupKey, groupRows);

    // ── Phone layout ────────────────────────────────────────────────────
    if (isPhone) {
        const cardRow = cardRowId === null ? undefined : rowsById.get(cardRowId);
        const cardIndex = cardRow === undefined ? -1 : visibleRows.indexOf(cardRow);
        const ctx: SheetCardContext = {
            close: () => setCardRowId(null),
            prev: () => { if (cardIndex > 0) setCardRowId(getRowId(visibleRows[cardIndex - 1])); },
            next: () => { if (cardIndex >= 0 && cardIndex < visibleRows.length - 1) setCardRowId(getRowId(visibleRows[cardIndex + 1])); },
            hasPrev: cardIndex > 0,
            hasNext: cardIndex >= 0 && cardIndex < visibleRows.length - 1,
            change: (changes) => applyChanges(changes, false),
        };
        const gridColumns = phoneColumns ?? visibleColumns;
        const renderPhoneRow = (row: R) => {
            const id = getRowId(row);
            const problems = errorsByRow.get(id);
            return (
                <tr key={String(id)} className={cx(cardRowId === id && classes.rowOpen, rowState?.(row) === "done" && classes.rowDone)}>
                    {gridColumns.map((column, c) => {
                        const value = columnValue(column, row);
                        const tone = column.tone?.(value, row);
                        const alignEnd = column.align === "end" || (column.align === undefined && isNumericColumn(column));
                        return (
                            <td key={column.key} className={frozenClass(c)} style={frozenStyle(c)}>
                                <button
                                    type="button"
                                    tabIndex={c === 0 ? 0 : -1}
                                    aria-label={c === 0 ? `Open ${labelOf(row)}` : undefined}
                                    className={cx("btn-unstyled", classes.cell, classes.phoneCell, alignEnd && classes.alignEnd, tone && TONE_CLASS[tone], tone && classes.toned,
                                        isBlank(value) && (column.emptyIsWarning ? classes.warnText : classes.mutedText),
                                        isCellChanged?.(row, column.key) && classes.changed,
                                        (problems?.some((p) => p.key === column.key) || (c === 0 && problems?.some((p) => p.key === null))) && classes.error)}
                                    onClick={() => setCardRowId(id)}
                                >
                                    <span className={classes.text}>{displayOf(column, row, value)}</span>
                                </button>
                            </td>
                        );
                    })}
                </tr>
            );
        };
        return (
            <div className={cx(classes.sheet, className)}>
                {(toolbarStart || toolbarEnd || toolbarTrail) && (
                    <div className={classes.toolbar}>
                        {toolbarStart}
                        <span className={classes.grow} />
                        {toolbarEnd}
                        {toolbarTrail}
                    </div>
                )}
                <div className={classes.phoneHint}>Swipe sideways for more columns · tap a {noun.one} to open it</div>
                <div className={classes.phoneBox}>
                    <table className={classes.grid} aria-label={ariaLabel}>
                        <colgroup>{gridColumns.map((column) => <col key={column.key} style={{ width: widthOf(column) }} />)}</colgroup>
                        <thead>
                            <tr>
                                {gridColumns.map((column, c) => (
                                    <th key={column.key} scope="col" className={cx(frozenClass(c), classes.phoneHead)} style={frozenStyle(c)}>{column.label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {groups ? groups.map((group) => (
                                <React.Fragment key={`g-${group.key}`}>
                                    <tr className={classes.groupRow}>
                                        <td colSpan={gridColumns.length}>
                                            <span className={classes.stickyLabel}>
                                                <strong>{groupLabelText(group.key, group.rows)}</strong>
                                                {groupBy?.note && <span>{groupBy.note(group.key, group.rows)}</span>}
                                            </span>
                                        </td>
                                    </tr>
                                    {group.rows.map(renderPhoneRow)}
                                </React.Fragment>
                            )) : sortedRows.map(renderPhoneRow)}
                            {!rows.length && (
                                <tr><td colSpan={gridColumns.length}><span className={classes.stickyLabel}>{emptyText ?? `No ${noun.many} yet.`}</span></td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {/* Phones: the totals as a stacked summary under the box, so no amount sits off-screen. */}
                {showTotals && (() => {
                    const head = gridColumns[0]?.total?.(filteredRows) ?? totalsLabel?.(filteredRows) ?? "Total";
                    const figures = gridColumns.slice(1)
                        .map((column) => ({ key: column.key, label: column.label, value: column.total?.(filteredRows) }))
                        .filter((figure) => figure.value !== undefined && figure.value !== null && figure.value !== "" && figure.value !== false);
                    return (
                        <div className={classes.phoneTotals} role="group" aria-label="Totals">
                            <div className={classes.phoneTotalsHead}>
                                <strong>Σ {head}</strong>
                                {totalsNote && <span>{totalsNote(filteredRows)}</span>}
                            </div>
                            {figures.length > 0 && (
                                <dl className={classes.phoneTotalsList}>
                                    {figures.map((figure) => (
                                        <div key={figure.key} className={classes.phoneTotalsRow}>
                                            <dt>{figure.label}</dt>
                                            <dd>{figure.value}</dd>
                                        </div>
                                    ))}
                                </dl>
                            )}
                        </div>
                    );
                })()}
                {cardRow !== undefined && (
                    <section className={classes.card} aria-label={labelOf(cardRow)}>
                        {renderCard ? renderCard(cardRow, ctx) : (
                            <DefaultCard
                                row={cardRow}
                                rowId={getRowId(cardRow)}
                                title={labelOf(cardRow)}
                                columns={visibleColumns}
                                ctx={ctx}
                                canEdit={canEditSheet}
                                isCellChanged={isCellChanged}
                                problems={errorsByRow.get(getRowId(cardRow)) ?? []}
                                idBase={idBase}
                                actions={cardActions?.(cardRow, ctx)}
                            />
                        )}
                    </section>
                )}
            </div>
        );
    }

    // ── Desktop layout ──────────────────────────────────────────────────
    const tableWidth = leadWidth + visibleColumns.reduce((total, column) => total + widthOf(column), 0) + (rowActions && !actionsFirst ? rowActionsWidth : 0) + (actionsFirst && rowTrailActions ? rowTrailActionsWidth : 0);
    const nameBoxValue = (() => {
        if (!activeColumn) return "";
        if (editing && !activeColumn.editor) return editing.draft;
        return activeRow === undefined ? "" : cellText(activeColumn, activeRow);
    })();
    const columnCount = visibleColumns.length + (rowActions ? 1 : 0);
    const trail = actionsFirst ? rowTrailActions : rowActions;
    const trailWidth = actionsFirst ? rowTrailActionsWidth : rowActionsWidth;
    const actionsLast = !!trail;

    let rowIndex = 0;
    const bodyRows: React.ReactNode[] = [];
    const pushRows = (list: R[]) => list.forEach((row) => {
        const r = rowIndex;
        rowIndex += 1;
        const id = getRowId(row);
        const state = rowState?.(row);
        bodyRows.push(
            <tr key={String(id)} className={cx(state === "done" && classes.rowDone, selected.has(id) && classes.rowSelected)}>
                {renderLeadCells("row", row, r)}
                {visibleColumns.map((column, c) => renderCell(row, r, column, c))}
                {actionsLast && <td className={classes.actionsCell}>{trail!(row)}</td>}
            </tr>,
        );
    });
    if (groups) {
        groups.forEach((group) => {
            const isOpen = !collapsed.has(group.key);
            bodyRows.push(
                <tr key={`g-${group.key}`} className={classes.groupRow}>
                    {renderLeadCells("group", undefined, undefined, group.rows)}
                    {renderSummaryCells(
                        (column) => column.subtotal?.(group.rows),
                        <button
                            type="button"
                            className={`btn-unstyled ${classes.groupToggle}`}
                            aria-expanded={isOpen}
                            title={isOpen ? "Collapse this group" : "Show this group's rows"}
                            onClick={() => setCollapsed((prev) => {
                                const next = new Set(prev);
                                if (next.has(group.key)) next.delete(group.key);
                                else next.add(group.key);
                                return next;
                            })}
                        >
                            <i className={`bi bi-chevron-${isOpen ? "down" : "right"}`} aria-hidden="true" />
                            <span>{groupLabelText(group.key, group.rows)}</span>
                        </button>,
                        groupBy?.note?.(group.key, group.rows),
                    )}
                    {actionsLast && <td />}
                </tr>,
            );
            if (isOpen) pushRows(group.rows);
        });
    } else {
        pushRows(sortedRows);
    }

    const newRowIndex = visibleRows.length;
    const pendingRows = pending?.lines.length ?? 0;
    const pendingColumns = pending ? pending.keys.map((key) => columns.find((c) => c.key === key)).filter((c): c is SheetColumn<R> => !!c) : [];

    return (
        <div className={cx(classes.sheet, className)}>
            {(toolbarStart || toolbarEnd || toolbarTrail || exportFileName || activeFilters.length > 0 || notice) && (
                <div className={classes.toolbar}>
                    {toolbarStart}
                    {activeFilters.map(([key, text]) => (
                        <Button key={key} type="button" size="xs" variant="secondary" icon="x-lg" title="Clear this filter"
                            onClick={() => setFilters((prev) => ({ ...prev, [key]: "" }))}>
                            {columns.find((c) => c.key === key)?.label}: “{text}”
                        </Button>
                    ))}
                    <span className={classes.grow} />
                    {notice && <span className={classes.notice} role="status">{notice}</span>}
                    {toolbarEnd}
                    <div ref={columnsMenuRef} className={classes.menuAnchor}>
                        <Button type="button" size="sm" variant="secondary" icon="layout-three-columns" title="Show or hide columns"
                            aria-haspopup="menu" aria-expanded={isColumnsOpen} onClick={() => setIsColumnsOpen((open) => !open)}>
                            Columns{hidden.size ? ` · ${hidden.size} hidden` : ""}
                        </Button>
                        {isColumnsOpen && (
                            <SheetPopover anchor={columnsMenuRef.current} align="end" onDismiss={() => setIsColumnsOpen(false)} className={classes.popover} role="menu" aria-label="Columns">
                                {columns.map((column) => (
                                    <label key={column.key} className={classes.popoverCheck}>
                                        <input type="checkbox" checked={!hidden.has(column.key)} onChange={() => toggleHidden(column.key)} />
                                        {column.label}
                                    </label>
                                ))}
                            </SheetPopover>
                        )}
                    </div>
                    {exportFileName && (
                        <Button type="button" size="sm" variant="secondary" icon="file-earmark-spreadsheet" title="Download these rows as an Excel file"
                            onClick={() => { exportSheetToExcel(exportFileName, visibleColumns, isExportable ? sortedRows.filter(isExportable) : sortedRows).catch(() => setNotice("Export failed — try again")); }}>
                            Export to Excel
                        </Button>
                    )}
                    {toolbarTrail}
                </div>
            )}

            {pending && (
                <div className={classes.pasteBar} role="status">
                    <i className="bi bi-clipboard-check" aria-hidden="true" />
                    <strong>Paste {pendingRows} {pendingRows === 1 ? "row" : "rows"} from Excel?</strong>
                    <span className={classes.pasteDetail}>
                        {[
                            pending.byHeader ? `Matched ${pending.matched} of ${columns.length} columns by their headers` : `Columns from ${pendingColumns[0]?.label ?? "the first"} on`,
                            ...pending.unmatched.map((title) => `“${title}” has no column here`),
                            pending.errorCount ? `${pending.errorCount} ${pending.errorCount === 1 ? "cell needs" : "cells need"} a fix — left empty` : "",
                        ].filter(Boolean).join(" · ")}
                    </span>
                    <span className={classes.grow} />
                    <Button type="button" size="sm" variant="secondary" title="See the rows before adding them" onClick={() => setShowPreview((open) => !open)}>
                        {showPreview ? "Hide preview" : "Preview"}
                    </Button>
                    <Button type="button" size="sm" variant="primary" icon="plus-lg" title={`Add them as new ${noun.many}`} onClick={addPending}>
                        Add {pendingRows} {pendingRows === 1 ? "row" : "rows"}
                    </Button>
                    <Button type="button" size="sm" variant="ghost" title="Don't add them" onClick={() => { setPending(null); setShowPreview(false); }}>
                        Cancel
                    </Button>
                </div>
            )}
            {pending && showPreview && (
                <div className={classes.preview}>
                    <table className={classes.previewGrid} aria-label="Rows to add">
                        <thead><tr>{pendingColumns.map((column) => <th key={column.key} scope="col">{column.label}</th>)}</tr></thead>
                        <tbody>
                            {pending.lines.map((line, index) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <tr key={index}>
                                    {pendingColumns.map((column) => {
                                        const cell = line[column.key];
                                        return (
                                            <td key={column.key} className={cx(cell?.error && classes.previewError)} title={cell?.error}>
                                                {cell?.text || <span className={classes.mutedText}>—</span>}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {selectable && selected.size > 0 && (
                <div className={classes.bulkBar} role="toolbar" aria-label={`Selected ${noun.many}`}>
                    <strong>{selected.size} {selected.size === 1 ? noun.one : noun.many} selected</strong>
                    <span className={classes.bulkHint}>Shift-click a box to select a range</span>
                    <span className={classes.grow} />
                    {bulkActions?.(selectedRows, clearSelection)}
                    <Button type="button" size="sm" variant="ghost" title="Unselect all" onClick={clearSelection}>Clear selection</Button>
                </div>
            )}

            {showNameBox && (
                <div className={classes.nameBar}>
                    <div className={classes.nameBox} aria-label="Active cell">
                        {activeColumn ? `${labelOf(activeRow)} · ${activeColumn.label}` : "—"}
                    </div>
                    <div className={classes.nameValue}>{nameBoxValue}</div>
                    <div className={classes.keys} aria-hidden="true">
                        <kbd>↑↓←→</kbd> move <kbd>Enter</kbd> edit <kbd>Tab</kbd> right <kbd>Esc</kbd> cancel <kbd>Ctrl Z</kbd> undo <kbd>Ctrl D</kbd> fill down <kbd>Ctrl V</kbd> paste
                    </div>
                </div>
            )}

            <div className={classes.box}>
                <table
                    ref={tableRef}
                    className={classes.grid}
                    style={{ width: tableWidth }}
                    role="grid"
                    aria-label={ariaLabel}
                    aria-rowcount={visibleRows.length}
                    aria-colcount={columnCount}
                    aria-activedescendant={visibleColumns.length ? `${idBase}-r${safeActive.r}-c${safeActive.c}` : undefined}
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onCopy={handleCopy}
                    onCut={handleCut}
                    onPaste={handlePaste}
                    onFocus={() => setHasFocus(true)}
                    onBlur={handleBlur}
                >
                    <colgroup>
                        {selectable && <col style={{ width: CHECK_W }} />}
                        {showRowNumbers && <col style={{ width: NUMBER_W }} />}
                        {actionsFirst && <col style={{ width: rowActionsWidth }} />}
                        {visibleColumns.map((column) => <col key={column.key} style={{ width: widthOf(column) }} />)}
                        {actionsLast && <col style={{ width: trailWidth }} />}
                    </colgroup>
                    <thead>
                        <tr>
                            {renderLeadCells("header")}
                            {visibleColumns.map((column, c) => {
                                const sortedHere = sort?.key === column.key ? sort.dir : 0;
                                const filtered = !!filters[column.key];
                                const alignEnd = column.align === "end" || (column.align === undefined && isNumericColumn(column));
                                const numeric = isNumericColumn(column) || column.type === "date" || column.type === "time";
                                return (
                                    <th
                                        key={column.key}
                                        scope="col"
                                        aria-sort={sortedHere === 1 ? "ascending" : sortedHere === -1 ? "descending" : undefined}
                                        className={cx(frozenClass(c), showRing && safeActive.c === c && classes.headOn, alignEnd && classes.alignEnd)}
                                        style={frozenStyle(c)}
                                    >
                                        <div className={classes.headWrap}>
                                            <button
                                                type="button"
                                                id={`${idBase}-head-${c}`}
                                                className={`btn-unstyled ${classes.headButton}`}
                                                title={column.title ? `${column.title} — click to sort, filter or hide` : "Click to sort, filter or hide"}
                                                aria-haspopup="menu"
                                                aria-expanded={headMenu === column.key}
                                                onClick={() => setHeadMenu((open) => (open === column.key ? null : column.key))}
                                            >
                                                <span className={classes.headLabel}>{column.label}</span>
                                                {sortedHere !== 0 && <i className={`bi bi-arrow-${sortedHere === 1 ? "up" : "down"}`} aria-hidden="true" />}
                                                {filtered && <i className="bi bi-funnel-fill" aria-hidden="true" />}
                                            </button>
                                            {headMenu === column.key && (
                                                <SheetPopover anchorId={`${idBase}-head-${c}`} onDismiss={() => setHeadMenu(null)} className={classes.popover} role="menu" aria-label={`${column.label} column`}>
                                                    {column.sortable !== false && (
                                                        <>
                                                            <button type="button" role="menuitem" className={`btn-unstyled ${classes.popoverItem}`} onClick={() => { setSort({ key: column.key, dir: 1 }); setHeadMenu(null); }}>
                                                                <i className="bi bi-sort-up" aria-hidden="true" />{numeric ? "Smallest / earliest first" : "Sort A → Z"}
                                                            </button>
                                                            <button type="button" role="menuitem" className={`btn-unstyled ${classes.popoverItem}`} onClick={() => { setSort({ key: column.key, dir: -1 }); setHeadMenu(null); }}>
                                                                <i className="bi bi-sort-down" aria-hidden="true" />{numeric ? "Largest / latest first" : "Sort Z → A"}
                                                            </button>
                                                            {sortedHere !== 0 && (
                                                                <button type="button" role="menuitem" className={`btn-unstyled ${classes.popoverItem}`} onClick={() => { setSort(null); setHeadMenu(null); }}>
                                                                    <i className="bi bi-x-lg" aria-hidden="true" />Clear sort
                                                                </button>
                                                            )}
                                                        </>
                                                    )}
                                                    {column.filterable !== false && (
                                                        <div className={classes.popoverFilter}>
                                                            <Input
                                                                type="text"
                                                                id={`${idBase}-filter-${column.key}`}
                                                                label="Show rows containing…"
                                                                value={filters[column.key] ?? ""}
                                                                className="form-control"
                                                                onChange={(_name, value) => setFilters((prev) => ({ ...prev, [column.key]: String(value) }))}
                                                            />
                                                        </div>
                                                    )}
                                                    <button type="button" role="menuitem" className={`btn-unstyled ${classes.popoverItem}`} disabled={visibleColumns.length <= 1}
                                                        onClick={() => { toggleHidden(column.key); setHeadMenu(null); }}>
                                                        <i className="bi bi-eye-slash" aria-hidden="true" />Hide column
                                                    </button>
                                                </SheetPopover>
                                            )}
                                        </div>
                                        <span className={classes.resizer} onPointerDown={(event) => startResize(event, column)} aria-hidden="true" title="Drag to resize" />
                                    </th>
                                );
                            })}
                            {actionsLast && <th scope="col"><span className="visually-hidden">Actions</span></th>}
                        </tr>
                    </thead>
                    <tbody>
                        {bodyRows}
                        {!rows.length && !hasNewRow && (
                            <tr><td colSpan={columnCount + (selectable ? 1 : 0) + (showRowNumbers ? 1 : 0)} className={classes.emptyRow}>{emptyText ?? `No ${noun.many} yet.`}</td></tr>
                        )}
                        {hasNewRow && (
                            <tr className={classes.newRow}>
                                {renderLeadCells("new")}
                                {visibleColumns.map((column, c) => {
                                    const isActive = safeActive.r === newRowIndex && safeActive.c === c;
                                    const ed = editing && editing.isNew && editing.c === c ? editing : null;
                                    return (
                                        <td
                                            key={column.key}
                                            id={`${idBase}-r${newRowIndex}-c${c}`}
                                            role="gridcell"
                                            aria-selected={isActive}
                                            className={cx(frozenClass(c), ed && classes.editing)}
                                            style={frozenStyle(c)}
                                            onMouseDown={(event) => handleCellMouseDown(event, newRowIndex, c)}
                                            onDoubleClick={() => startEdit({ r: newRowIndex, c }, null)}
                                        >
                                            {ed ? renderEditor(undefined, column, ed) : (
                                                <div className={cx(classes.cell, isActive && showRing && classes.active)}>
                                                    {c === 0 && <span className={classes.newRowHint}>{newRowLabel ?? `Type here to add a ${noun.one} — or paste rows from Excel`}</span>}
                                                </div>
                                            )}
                                        </td>
                                    );
                                })}
                                {actionsLast && <td />}
                            </tr>
                        )}
                    </tbody>
                    {showTotals && (
                        <tfoot>
                            <tr className={classes.totalsRow}>
                                {renderLeadCells("total")}
                                {renderSummaryCells(
                                    (column) => column.total?.(filteredRows),
                                    visibleColumns[0]?.total?.(filteredRows) ?? totalsLabel?.(filteredRows) ?? "Total",
                                    totalsNote?.(filteredRows),
                                )}
                                {actionsLast && <td />}
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </div>
    );
}

interface DefaultCardProps<R> {
    row: R;
    rowId: SheetRowId;
    title: string;
    columns: SheetColumn<R>[];
    ctx: SheetCardContext;
    canEdit: boolean;
    isCellChanged?: (row: R, key: string) => boolean;
    problems: { key: string | null; message: string }[];
    idBase: string;
    actions?: React.ReactNode;
}

/** A phone field for text-like columns: keeps what's typed, applies it once it parses. */
const CardTextField: React.FC<{
    id: string;
    label: string;
    initial: string;
    isChanged: boolean;
    problem?: string;
    parse: (text: string) => { value: unknown } | { error: string };
    onValue: (value: unknown) => void;
}> = ({ id, label, initial, isChanged, problem, parse, onValue }) => {
    const [draft, setDraft] = useState(initial);
    const [error, setError] = useState<string | null>(null);
    return (
        <Input
            type="text"
            id={id}
            label={label}
            value={draft}
            className="form-control"
            isChanged={isChanged}
            isInvalid={!!(error || problem)}
            errorMessage={error ?? problem}
            onChange={(_name, value) => {
                const text = String(value);
                setDraft(text);
                const parsed = parse(text);
                if ("error" in parsed) setError(parsed.error);
                else {
                    setError(null);
                    onValue(parsed.value);
                }
            }}
        />
    );
};

/** The card a tapped row opens on phones when the page gives no renderCard: one field per column. */
function DefaultCard<R>({ row, rowId, title, columns, ctx, canEdit, isCellChanged, problems, idBase, actions }: DefaultCardProps<R>) {
    const rowProblem = problems.find((p) => p.key === null)?.message;
    return (
        <>
            <div className={classes.cardHead}>
                <h3 className={classes.cardTitle}>{title}</h3>
                <span className={classes.grow} />
                <Button type="button" size="sm" variant="secondary" icon="chevron-left" disabled={!ctx.hasPrev} onClick={ctx.prev} title="The row above">Previous</Button>
                <Button type="button" size="sm" variant="secondary" icon="chevron-right" disabled={!ctx.hasNext} onClick={ctx.next} title="The row below">Next</Button>
            </div>
            {rowProblem && <div className={classes.cardProblem} role="alert">{rowProblem}</div>}
            <div className={classes.cardFields}>
                {columns.filter((column) => !column.hideOnCard).map((column) => {
                    const value = columnValue(column, row);
                    const changed = !!isCellChanged?.(row, column.key);
                    const problem = problems.find((p) => p.key === column.key)?.message;
                    const set = (next: unknown) => ctx.change([{ rowId, key: column.key, value: next }]);
                    const fieldId = `${idBase}-card-${column.key}`;
                    if (!canEdit || isReadOnly(column, row)) {
                        return (
                            <div key={column.key} className={classes.readField}>
                                <span className={classes.readLabel}>{column.label}</span>
                                <span className={classes.readValue}>{displayOf(column, row, value)}</span>
                            </div>
                        );
                    }
                    if (column.editor) {
                        return (
                            <div key={column.key} className={classes.cardCustom}>
                                <span className={classes.readLabel}>{column.label}</span>
                                {column.editor({ row, value, onChange: set, onCommit: (next) => { if (next !== undefined) set(next); }, onCancel: () => undefined })}
                            </div>
                        );
                    }
                    if (column.type === "select") {
                        const options = columnOptions(column, row);
                        return (
                            <Input
                                key={column.key}
                                type="select"
                                id={fieldId}
                                label={column.label}
                                value={isBlank(value) ? "" : String(value)}
                                options={options.map((option) => ({ value: String(option.value), label: option.hint ? `${option.label} · ${option.hint}` : option.label }))}
                                isChanged={changed}
                                isInvalid={!!problem}
                                errorMessage={problem}
                                onChange={(_name, next) => {
                                    const option = options.find((o) => String(o.value) === String(next));
                                    set(option ? option.value : null);
                                }}
                            />
                        );
                    }
                    if (column.type === "date") {
                        return (
                            <Input key={column.key} type="date" id={fieldId} label={column.label} value={isBlank(value) ? "" : String(value)}
                                className="form-control" isChanged={changed} isInvalid={!!problem} errorMessage={problem}
                                onChange={(_name, next) => set(next ? String(next) : null)} />
                        );
                    }
                    return (
                        <CardTextField
                            key={`${String(rowId)}-${column.key}`}
                            id={fieldId}
                            label={column.label}
                            initial={editTextOf(column, row)}
                            isChanged={changed}
                            problem={problem}
                            parse={(text) => parseCellText(column, text, row)}
                            onValue={set}
                        />
                    );
                })}
            </div>
            <div className={classes.cardFoot}>
                <Button type="button" size="sm" variant="ghost" icon="arrow-left" onClick={ctx.close} title="Back to the rows">Back to the sheet</Button>
                {actions}
            </div>
        </>
    );
}

export default DataSheet;
